import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const projects = [
  {
    before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/4jsedrwx_IMG_0695.png",
    after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/oru8lnou_IMG_0696.png",
    altBefore: "Blocked gutters with pigeon droppings under solar panels",
    altAfter: "Clean gutters after professional gutter clean service"
  },
  {
    before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/h62hrxig_IMG_1139.jpeg",
    after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/fyli5lun_IMG_1138.jpeg",
    altBefore: "Heavy bird droppings and debris on flat roof solar panels",
    altAfter: "Clean roof with solar proofing mesh installed"
  },
  {
    before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/orqq6r1b_IMG_6862.jpeg",
    after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/aukjev1y_IMG_6879.jpeg",
    altBefore: "Bird netting damage with droppings on commercial building",
    altAfter: "Clean bird netting repair on commercial building"
  },
  {
    before: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/w1rw9fzr_IMG_5474.jpeg",
    after: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/g9xqnd56_IMG_5486.jpeg",
    altBefore: "Dirty solar panels with bird droppings on tile roof",
    altAfter: "Clean solar panels after professional panel wash"
  },
  {
    before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/65g4836q_IMG_2924.jpeg",
    after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/9vkz1ylq_IMG_2950.jpeg",
    altBefore: "Solar panels before solar proofing mesh installation",
    altAfter: "Solar panels with premium mesh protection installed"
  },
  {
    before: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/tuhw31zd_IMG_5759.jpeg",
    after: "https://customer-assets.emergentagent.com/job_bird-mesh-install/artifacts/6e943s9p_IMG_5764.jpeg",
    altBefore: "Dirty tile roof under solar panels with debris",
    altAfter: "Solar panels with solar proofing mesh protection"
  }
];

const installPhotos = [
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/z5jjmahy_IMG_1795.jpeg", caption: "Stainless steel mesh & clip detail" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/uyek99ws_IMG_5432.jpeg", caption: "Mesh along roof edge" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/1g87oe1v_IMG_0886.png", caption: "Install on new panels" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/tet1ecjw_IMG_8783.jpeg", caption: "Panel cleaning with water-fed brush" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/w6vcbets_IMG_1037.jpeg", caption: "Early-stage bird proofing", pos: "bottom" },
  { url: "https://customer-assets.emergentagent.com/job_83a514b1-4177-45df-874c-08a2db83c63f/artifacts/7avvtv1a_IMG_0716.jpeg", caption: "Cleaned panels with mesh" },
];

export default function GutterCleanGallery() {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c - 1 + projects.length) % projects.length);
  const next = () => setCurrent((c) => (c + 1) % projects.length);

  return (
    <section className="py-12 bg-white" data-testid="gutter-clean-gallery">
      <div className="max-w-5xl mx-auto px-4">
        {/* Installation Photos First */}
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-800">Our Installation Work</h2>
          <p className="text-slate-600 mt-2">See how we protect your solar investment with precision-fitted mesh</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
          {installPhotos.map((photo, i) => (
            <div key={i} className="group relative rounded-lg overflow-hidden shadow aspect-square">
              <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" style={photo.pos ? {objectPosition: photo.pos} : undefined} loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
                <p className="text-white text-[10px] md:text-xs font-medium leading-tight">{photo.caption}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Before & After Carousel */}
        <div className="mt-12 text-center mb-6">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-800">
            Real Results — <span className="text-[#7ABC32]">Before & After</span>
          </h3>
          <p className="text-slate-600 mt-2">See the difference our gutter cleaning and bird proofing makes</p>
        </div>

        <div className="relative">
          <div className="flex">
            {/* Before */}
            <div className="relative flex-1">
              <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">BEFORE</div>
              <div className="rounded-l-xl overflow-hidden">
                <img src={projects[current].before} alt={projects[current].altBefore} className="w-full h-[240px] sm:h-[320px] md:h-[400px] object-cover" />
              </div>
            </div>

            {/* Divider */}
            <div className="w-[3px] bg-white z-10 shrink-0" />

            {/* After */}
            <div className="relative flex-1">
              <div className="absolute top-3 right-3 z-10 bg-[#7ABC32] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">AFTER</div>
              <div className="rounded-r-xl overflow-hidden">
                <img src={projects[current].after} alt={projects[current].altAfter} className="w-full h-[240px] sm:h-[320px] md:h-[400px] object-cover" />
              </div>
            </div>
          </div>

          {/* Left Arrow */}
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-all">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          {/* Right Arrow */}
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full transition-all">
            <ChevronRight className="w-6 h-6 text-white" />
          </button>

          {/* Counter */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/40 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
            {current + 1} / {projects.length}
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-4">
          {projects.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-[#5BC0DE] w-7' : 'bg-slate-300 hover:bg-slate-400'}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
